import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { PrimeIcons } from 'primereact/api'
import { Button } from 'primereact/button'
import { Menu } from 'primereact/menu'
import { MenuItem } from 'primereact/menuitem'
import { Messages } from 'primereact/messages'

import { CheckRequiredFields } from '../../../../components/CheckRequiredFields'
import { Header } from '../../../../components/Header'
import { OrderResponse } from '../../../../data/models'
import { WebEdiDto } from '../../../../data/models/WebEdiDto'
import {
  getVisibleNoteParams,
  getVisibleNotes,
} from '../../../../utils/helper/getVisibleNotes'
import { useConfigContext } from '../../../ui-config/context'
import { useOrder } from '../../data'
import { Accept, Reject } from './dialogs'

interface Props {
  id: WebEdiDto<OrderResponse>['Id']
  orderId: OrderResponse['OrderId']
}

interface AcceptDialogState {
  isOpen: boolean
  isPreliminary: boolean
}

const Title: React.FC<Props> = ({ id, orderId }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const menuRef = useRef<Menu | null>(null)

  const { data: order, isLoading } = useOrder(id)
  const { uiConfig: config } = useConfigContext()

  const [displayRejectOrder, setDisplayRejectOrder] = useState(false)
  const [displayAcceptOrder, setDisplayAcceptOrder] =
    useState<AcceptDialogState>({ isOpen: false, isPreliminary: false })

  const messagesRef = useRef<Messages>(null)

  useEffect(() => {
    if (!messagesRef) return

    const messages = getVisibleNotes(order?.Payload, 'ORDRSP')

    if (!messages) return

    messages.forEach(message => {
      const params = getVisibleNoteParams(message)

      messagesRef.current?.replace({
        id: message.HintId,
        sticky: true,
        severity: 'warn',
        closable: false,
        icon: PrimeIcons.EXCLAMATION_CIRCLE,
        summary: t(`common.visibleNotes.${message.HintId}.summary`),
        detail: t(`common.visibleNotes.${message.HintId}.detail`, params),
      })
    })
  }, [t, messagesRef, order?.Payload])

  const menuItems: MenuItem[] = [
    {
      label: t('orderResponse.title.send.label_prelim'),
      icon: PrimeIcons.CHECK,
      command: () =>
        setDisplayAcceptOrder({ isOpen: true, isPreliminary: true }),
    },
    {
      label: t('orderResponse.title.send.label_final'),
      icon: PrimeIcons.CHECK_SQUARE,
      command: () =>
        setDisplayAcceptOrder({ isOpen: true, isPreliminary: false }),
    },
  ]

  const setAcceptDialogVisibility = (visible: boolean) => {
    setDisplayAcceptOrder({
      isOpen: visible,
      isPreliminary: displayAcceptOrder.isPreliminary,
    })
  }

  const actions = (
    <>
      <Button
        label={t('orderResponse.title.history.label')}
        className="p-button-text"
        onClick={() =>
          navigate(`/orders/history?search=${order?.Payload.OrderId}`)
        }
      />

      <Button
        label={t('orderResponse.title.reject.label')}
        icon="pi pi-times"
        className="p-button-outlined"
        onClick={() => setDisplayRejectOrder(true)}
        visible={config.orderResponse.rejectOrder.enabled !== false}
      />

      <CheckRequiredFields data={order}>
        {(isDisabled, tooltip) => (
          <>
            {tooltip}
            {order?.ProcessControl?.AllowPreliminaryConfirmation ? (
              <Button
                label={t('orderResponse.title.send.label')}
                icon="pi pi-check"
                className="send-button"
                onClick={e => menuRef.current?.toggle(e)}
                disabled={isDisabled}
                visible={config.orderResponse.acceptOrder.enabled !== false}
                aria-controls="popup_menu_right"
                aria-haspopup
              />
            ) : (
              <Button
                label={t('orderResponse.title.send.label')}
                icon="pi pi-check"
                className="send-button"
                onClick={() =>
                  setDisplayAcceptOrder({ isOpen: true, isPreliminary: false })
                }
                disabled={isDisabled}
                visible={config.orderResponse.acceptOrder.enabled !== false}
              />
            )}
          </>
        )}
      </CheckRequiredFields>

      <Reject
        visible={displayRejectOrder}
        setVisible={setDisplayRejectOrder}
        id={id}
      />

      <Accept
        visible={displayAcceptOrder.isOpen}
        preliminary={displayAcceptOrder.isPreliminary}
        setVisible={setAcceptDialogVisibility}
        id={id}
        currency={order?.Payload.Currency!}
      />

      <Menu
        id="menu_send_orderresponse"
        className="surface-overlay"
        ref={menuRef}
        model={menuItems}
        popupAlignment="right"
        popup
      />
    </>
  )

  return (
    <>
      <Header
        title={t('orderResponse.title.title', {
          orderId: orderId,
        })}
        showBackButton={true}
        showPrintButton={true}
        actions={actions}
        isLoading={isLoading}
      />

      <Messages ref={messagesRef} />
    </>
  )
}

export default Title
